import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  CloseButton,
  Button,
  Input,
  Textarea,
  NativeSelect,
  Switch,
  VStack,
  HStack,
  Text,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../shared'
import { useCreateTag, useUpdateTag } from '../../hooks/useTag'
import type { SaveTagRequest, TagDetailDto } from '../../types/tag'
import { TAG_TYPES } from '../../types/tag'

interface TagFormModalProps {
  isOpen: boolean
  onClose: () => void
  tag?: TagDetailDto | null
}

export function TagFormModal({ isOpen, onClose, tag }: TagFormModalProps) {
  const isEdit = !!tag
  const { register, handleSubmit, reset, watch, setValue } = useForm<SaveTagRequest>({
    defaultValues: {
      label: '',
      labelSpanish: '',
      description: '',
      descriptionSpanish: '',
      scriptText: '',
      scriptTextSpanish: '',
      tagType: '',
      isVisible: true,
      canBeAssigned: true,
      isOrganizationTag: false,
    },
  })

  const createMutation = useCreateTag()
  const updateMutation = useUpdateTag(tag?.id || '')

  const isVisible = watch('isVisible')
  const canBeAssigned = watch('canBeAssigned')
  const isOrganizationTag = watch('isOrganizationTag')

  useEffect(() => {
    if (tag) {
      reset({
        label: tag.label,
        labelSpanish: tag.labelSpanish || '',
        description: tag.description || '',
        descriptionSpanish: tag.descriptionSpanish || '',
        scriptText: tag.scriptText || '',
        scriptTextSpanish: tag.scriptTextSpanish || '',
        tagType: tag.tagType || '',
        isVisible: tag.isVisible,
        canBeAssigned: tag.canBeAssigned,
        isOrganizationTag: tag.isOrganizationTag,
      })
    } else {
      reset({
        label: '',
        labelSpanish: '',
        description: '',
        descriptionSpanish: '',
        scriptText: '',
        scriptTextSpanish: '',
        tagType: '',
        isVisible: true,
        canBeAssigned: true,
        isOrganizationTag: false,
      })
    }
  }, [tag, reset])

  const onSubmit = async (data: SaveTagRequest) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data)
        toaster.create({
          title: 'Tag updated',
          description: 'The tag has been updated successfully.',
          type: 'success',
          duration: 3000,
        })
      } else {
        await createMutation.mutateAsync(data)
        toaster.create({
          title: 'Tag created',
          description: 'The tag has been created successfully.',
          type: 'success',
          duration: 3000,
        })
      }
      onClose()
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Failed to ${isEdit ? 'update' : 'create'} tag. Please try again.`,
        type: 'error',
        duration: 5000,
      })
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxH="90vh">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Dialog.Header>{isEdit ? 'Edit Tag' : 'Create New Tag'}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

            <Dialog.Body maxH="calc(90vh - 140px)" overflowY="auto">
              <VStack gap={4} align="stretch">
                <Field.Root required>
                  <Field.Label fontSize="sm">Label</Field.Label>
                  <Input size="sm" {...register('label', { required: true })} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Label (Spanish)</Field.Label>
                  <Input size="sm" {...register('labelSpanish')} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Tag Type</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field {...register('tagType')} placeholder="Select type">
                      {TAG_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Description</Field.Label>
                  <Textarea size="sm" rows={3} {...register('description')} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Description (Spanish)</Field.Label>
                  <Textarea size="sm" rows={3} {...register('descriptionSpanish')} />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Script Text</Field.Label>
                  <Textarea
                    size="sm"
                    rows={3}
                    {...register('scriptText')}
                    placeholder="Text shown in script when this tag is present"
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">Script Text (Spanish)</Field.Label>
                  <Textarea size="sm" rows={3} {...register('scriptTextSpanish')} />
                </Field.Root>

                <VStack gap={3} align="stretch" pt={2}>
                  <Field.Root>
                    <HStack justify="space-between">
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">Visible to Canvassers</Text>
                        <Text fontSize="xs" color="gray.500">Show this tag to canvassers</Text>
                      </VStack>
                      <Switch.Root
                        checked={isVisible}
                        onCheckedChange={(e) => setValue('isVisible', e.checked === true)}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </HStack>
                  </Field.Root>

                  <Field.Root>
                    <HStack justify="space-between">
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">Can Be Assigned</Text>
                        <Text fontSize="xs" color="gray.500">Canvassers can add/remove this tag</Text>
                      </VStack>
                      <Switch.Root
                        checked={canBeAssigned}
                        onCheckedChange={(e) => setValue('canBeAssigned', e.checked === true)}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </HStack>
                  </Field.Root>

                  <Field.Root>
                    <HStack justify="space-between">
                      <VStack align="start" gap={0}>
                        <Text fontSize="sm" fontWeight="medium">Organization Tag</Text>
                        <Text fontSize="xs" color="gray.500">Can be assigned to organizations</Text>
                      </VStack>
                      <Switch.Root
                        checked={isOrganizationTag}
                        onCheckedChange={(e) => setValue('isOrganizationTag', e.checked === true)}
                      >
                        <Switch.HiddenInput />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    </HStack>
                  </Field.Root>
                </VStack>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose} size="sm">
                Cancel
              </Button>
              <Button
                colorPalette="brand"
                type="submit"
                loading={isLoading}
                size="sm"
              >
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
